import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import Pitch from "./components/Pitch";
import Ballot from "./components/Ballot";
import WatchStage from "./components/WatchStage";
import AuthContext from "../../contexts/AuthContext";
import request from "../../utilities/requests";
import { isNotSingle, clearNotSingle } from "../../utilities/dealbreakers";

const WAITING_RETRY_MS = 10000;
const HEARTBEAT_MS = 25000;
const HEARTBEAT_JITTER_MS = 10000;

const DealBreakers = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { socket } = useContext(AuthContext);
  const spectator = isNotSingle(eventId);

  const [stage, setStage] = useState(null);
  const [contestant, setContestant] = useState(null);
  const [round, setRound] = useState(null);
  const [like, setLike] = useState(false);
  const [canLike, setCanLike] = useState(false);
  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(false);

  const seq = useRef(0);

  const getRound = useCallback(async () => {
    if (!eventId) return;
    const mine = ++seq.current;
    try {
      const result = await request(`dealbreakers/round?eventId=${eventId}`);
      if (mine !== seq.current) return;
      if (result?.success) {
        setStage(result.data.stage);
        setContestant(result.data.contestant);
        setRound(result.data.round);
        setLike(result.data.like);
        setCanLike(result.data.canLike);
        setWaiting(false);
      } else {
        setWaiting(true);
      }
    } catch (e) {
      if (mine !== seq.current) return;
      setWaiting(true);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    getRound();
  }, [getRound]);

  useEffect(() => {
    if (!socket || !eventId) return;

    const join = () => socket.emit("joinEvent", eventId);

    const onReconnect = () => {
      join();
      getRound();
    };

    const onStageUpdate = (data) => {
      const newStage = data?.newStage;
      if (newStage) setStage(newStage);
      getRound();
    };

    join();
    socket.on("connect", onReconnect);
    socket.on("stageUpdate", onStageUpdate);

    return () => {
      socket.off("connect", onReconnect);
      socket.off("stageUpdate", onStageUpdate);
    };
  }, [socket, eventId, getRound]);

  useEffect(() => {
    if (waiting) {
      const t = setInterval(getRound, WAITING_RETRY_MS);
      return () => clearInterval(t);
    }

    let timer;
    const tick = () => {
      if (socket && socket.disconnected) socket.connect();
      getRound();
      timer = setTimeout(tick, HEARTBEAT_MS + Math.random() * HEARTBEAT_JITTER_MS);
    };
    timer = setTimeout(tick, HEARTBEAT_MS + Math.random() * HEARTBEAT_JITTER_MS);
    return () => clearTimeout(timer);
  }, [waiting, getRound, socket]);

  useEffect(() => {
    if (!socket) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (socket.disconnected) socket.connect();
      getRound();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [socket, getRound]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={80} />
      </Box>
    );
  }

  if (stage === "voting") {
    return <Ballot eventId={eventId} />;
  }

  if (stage === "pitching") {
    return (
      <Pitch
        eventId={eventId}
        contestant={contestant}
        round={round}
        like={like}
        setLike={setLike}
        canLike={spectator ? false : canLike}
      />
    );
  }

  if (spectator) {
    const goSingle = () => {
      clearNotSingle(eventId);
      navigate("/");
    };
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          px: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5">Enjoy the show!</Typography>
        <Typography color="textSecondary">
          Come back here when the show starts. Once contestants take the stage
          you can watch and vote right from here.
        </Typography>
        <Button variant="outlined" size="small" onClick={goSingle} sx={{ mt: 2 }}>
          Actually, I am single
        </Button>
      </Box>
    );
  }

  if (waiting) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          px: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">Hang tight</Typography>
        <Typography color="textSecondary">
          The next contestant is getting ready. This screen will update on its
          own.
        </Typography>
      </Box>
    );
  }

  return <WatchStage />;
};

export default DealBreakers;
